import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import base64 from "base-64";
import Cookies from "js-cookie";
import { merge } from "lodash";

// import { ApiError, isAxiosError } from "@/ui/api/apiError";
// import { COOKIE_KEYS } from "@/ui/constants/cookie";

import { ApiError, isAxiosError } from "./apiError";
import { COOKIE_KEYS } from "../components/constants/cookie";
type HTTPClientOptions = AxiosRequestConfig & {
  baseURL: string;
  cookieKey?: string;
  authorized?: boolean;
  removeBaseAuthHeader?: boolean;
};

export class HTTPClient {
  baseURL: string;
  cookieKey: string;
  private useOldMiddleware: boolean;
  private authorized: boolean;
  private removeBaseAuthHeader: boolean;
  private axiosInstance: AxiosInstance;

  constructor({
    url,
    cookieKey,
    authorized,
    removeBaseAuthHeader,
    ...axiosOptions
  }: HTTPClientOptions) {
    this.baseURL = url || axiosOptions.baseURL;
    this.cookieKey = cookieKey || COOKIE_KEYS.WINDSTREAM_CART_TOKEN;
    this.useOldMiddleware =
      process.env.REACT_APP_USE_OLD_MIDDLEWARE === "true";
    this.authorized = authorized || false;
    this.removeBaseAuthHeader = removeBaseAuthHeader || false;

    this.axiosInstance = axios.create(
      merge(
        {
          url,
          headers: {
            "Content-type": "application/json",
          },
        },
        axiosOptions,
      ),
    );
  }

  getCartToken = () => Cookies.get(this.cookieKey) ?? "";

  private getAuthHeaders = () => {
    if (this.useOldMiddleware) {
      return {
        Authorization: !this.removeBaseAuthHeader
          ? `Basic ${base64.encode("union:union")}`
          : undefined,
        "X-Kinetic-Cart-Token": this.authorized
          ? this.getCartToken()
          : undefined,
      };
    } else {
      return {
        Authorization: this.authorized
          ? `Bearer ${this.getCartToken()}`
          : undefined,
      };
    }
  };

  private baseRequest<T>(options: AxiosRequestConfig<T>) {
    return this.axiosInstance
      .request({
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      })
      .then(response =>
        Promise.resolve(response.status === 204 ? null : response.data),
      )
      .catch(err => {
        if (isAxiosError(err)) {
          return Promise.reject(new ApiError(err));
        } else {
          return err?.response ?? err;
        }
      });
  }

  get<T>(
    url: string,
    params = {},
    options: AxiosRequestConfig<T> = {},
  ): Promise<T> {
    return this.baseRequest({ ...options, method: "GET", url, params });
  }

  post<T>(
    url: string,
    data: any,
    options: AxiosRequestConfig<T> = {},
  ): Promise<T> {
    return this.baseRequest({ ...options, method: "POST", data, url });
  }

  patch<T>(
    url: string,
    data: any,
    options: AxiosRequestConfig<T> = {},
  ): Promise<T> {
    return this.baseRequest({ ...options, method: "PATCH", data, url });
  }

  put<T>(
    url: string,
    data: any,
    options: AxiosRequestConfig<T> = {},
  ): Promise<T> {
    return this.baseRequest({ ...options, method: "PUT", data, url });
  }

  // Named deleteRequest because delete is a reserved keyword
  deleteRequest<T>(
    url: string,
    data: any,
    options: AxiosRequestConfig<T> = {},
  ): Promise<T> {
    return this.baseRequest({ ...options, method: "DELETE", data, url });
  }
}
